import {socialAuthIntegrationSchema} from '@practicegenius/domain/src/data/integrations';
import {z} from 'zod';
import {buildUrl, resolveRoute} from '@practicegenius/pg-util/src/UrlUtils';
import qs from 'qs';
import {booleanSchema} from '@practicegenius/pg-util/src/ZodUtils';
import {loginPerspectiveSchema} from './loginPerspective';
import {memberDestinationSchema} from './memberDestination';
import {practiceUserLoginMigrationTokenSchema} from './practiceUserLoginMigration';

/** Subdomain of Central Identity Service */
const ID_SUBDOMAIN = 'id';
const ID_DOMAIN = 'culturello';

export const idDotLoginRoutes = {
  index: '/login',
  social: {
    login: '/login/social_provider/:provider',
    callback: '/login/social_provider/callback',
  },
  fromAuthMigrationToken: '/login/fromAuthMigrationToken',
};

export const idDotRegistrationRoutes = {
  authenticate: '/registration/authenticate',
};

export const idDotApiRoutesForLogin = {
  authRefresh: '/api/s2s/authRefresh',
  confirmLogin: '/api/s2s/confirmLogin',
};

export const idDotSharedAuthenticationRoutes = {
  /** Handles authenticating a downstream app according to query params and redirecting to it */
  authenticateToApp: '/app/authenticateToApp',
};

export const idDotLogoutRoutes = {
  logout: '/app/logout',
};

export const idDotUserRoutes = {
  profile: '/app/user/profile',
  linkedUserProfile: '/app/user/linked-profile/:linkedUserId',
  linkedFamily: '/app/user/linked-family',
};

export const idDotInviteRoutesAuthenticated = {
  /** Authenticated invite page - requires user to be logged in */
  inviteLink: '/app/invite/:inviteCode',
};

export const idDotInviteRoutesUnauthenticated = {
  /** Unauthenticated invite entry point - handles login flow and redirects to authenticated page */
  inviteLinkUnauth: '/invite/:inviteCode',
};

const loginErrorQueryParamSchema = z.union([
  z.object({
    errorType: z.literal('unregistered_social_account'),
    provider: socialAuthIntegrationSchema,
  }),
  z.object({
    errorType: z.literal('generic'),
  }),
  z.object({
    errorType: z.literal('registration_not_finished'),
    token: z.string(),
    viaSocial: booleanSchema.optional(),
  }),
  z.object({}),
]);

export const loginRejectAfterSuccessReasonSchema = z.literal('denied');
export type LoginRejectAfterSuccessReason = z.infer<
  typeof loginRejectAfterSuccessReasonSchema
>;
export const idDotLoginQueryParamsSchema = z.intersection(
  loginErrorQueryParamSchema,
  z.object({
    referrer: z.string().optional(),
    loginPerspective: loginPerspectiveSchema.optional(),
    rejectAfterSuccess: loginRejectAfterSuccessReasonSchema.optional(),
    hideBackButton: booleanSchema.optional(),
    memberDestination: memberDestinationSchema.optional(),
    previewMarketingId: z.string().optional(),
    /** When true, the username field will be disabled/locked (captive login) */
    isCaptive: booleanSchema.optional(),
  }),
);

export const buildIdBaseUrlFromCurrentUrl = (currentUrl: string) => {
  const url = new URL(currentUrl);
  const [, , tld] = url.hostname.split('.');
  url.hostname = `${ID_SUBDOMAIN}.${ID_DOMAIN}.${tld}`;
  return url;
};

/**
 * Generic function to build ID dot URLs for any route
 */
export const buildIdDotUrl = (
  /** Current URL from window or host header */
  currentUrl: string,
  /**
   * Route in id-dot. If this contains query params they will be merged with the queryParams parameter if any are specified there.
   * If there are duplicated params between the route and the queryParams parameter, the queryParams parameter will take precedence.
   */
  route: string,
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  const idUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  return buildUrl({route, origin: idUrl.origin, queryParams});
};

export type IdDotLoginQueryParams = z.infer<typeof idDotLoginQueryParamsSchema>;

export const buildIdLoginUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLoginQueryParams,
): string => {
  const loginUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  loginUrl.pathname = idDotLoginRoutes.index;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      const serializedValue =
        typeof value === 'boolean' || typeof value === 'number'
          ? value.toString()
          : value;
      loginUrl.searchParams.set(key, serializedValue);
    }
  }
  return loginUrl.toString();
};

export const idDotLogoutQueryParamsSchema = z.object({
  /** Perspective to show on the login screen after logout */
  loginPerspective: loginPerspectiveSchema.optional(),
  /** Redirect to this URL instead of the login page after logout */
  redirect: z.string().optional(),
});
export type IdDotLogoutQueryParams = z.infer<
  typeof idDotLogoutQueryParamsSchema
>;

export const buildIdLogoutUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLogoutQueryParams = {},
): string => {
  const logoutUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  logoutUrl.pathname = idDotLogoutRoutes.logout;
  logoutUrl.search = qs.stringify(params);
  return logoutUrl.toString();
};

export const idDotRegistrationAuthCodeParamsSchema = z.object({
  registrationAuthCode: z.string(),
  /** fully qualified route to redirect to after successful login */
  referrer: z.string().optional(),
});
export type IdDotRegistrationAuthCodeParams = z.infer<
  typeof idDotRegistrationAuthCodeParamsSchema
>;

export const buildIdRegistrationUrl = (
  currentUrl: string,
  params: IdDotRegistrationAuthCodeParams,
): string => {
  const registrationUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  registrationUrl.pathname = idDotRegistrationRoutes.authenticate;
  registrationUrl.search = qs.stringify(params);
  return registrationUrl.toString();
};

export const fromAuthMigrationTokenPageParamsSchema = z.union([
  z.object({
    token: practiceUserLoginMigrationTokenSchema,
    /** fully qualified route to redirect to after successful login */
    referrer: z.string(),
  }),
  z.object({
    token: practiceUserLoginMigrationTokenSchema,
  }),
]);
export type FromAuthMigrationTokenPageParams = z.infer<
  typeof fromAuthMigrationTokenPageParamsSchema
>;

export const buildFromAuthMigrationTokenPageUrl = (
  currentUrl: string,
  params: FromAuthMigrationTokenPageParams,
) => {
  const fromAuthMigrationTokenUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  fromAuthMigrationTokenUrl.pathname = idDotLoginRoutes.fromAuthMigrationToken;
  fromAuthMigrationTokenUrl.search = qs.stringify(params);
  return fromAuthMigrationTokenUrl.toString();
};

export const idDotInviteQueryParamsSchema = z.object({
  inviteCode: z.string(),
});
export type IdDotInviteQueryParams = z.infer<
  typeof idDotInviteQueryParamsSchema
>;

export const buildIdInviteUrl = (currentUrl: string, inviteCode: string) => {
  const inviteUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  // Use the unauthenticated entry point - it will handle authentication flow
  inviteUrl.pathname = resolveRoute(
    idDotInviteRoutesUnauthenticated.inviteLinkUnauth,
    {
      inviteCode,
    },
  );
  return inviteUrl.toString();
};
